import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Palette } from 'lucide-react';
import { brandPresets, type BrandPreset } from '../../lib/panelBrand';
import { useTheme } from '../../lib/useTheme';

/**
 * Selector del preset de marca del panel. Cambia la familia de color `ui-*`
 * (vía data-brand-preset) al instante y la persiste. Cada opción muestra una
 * vista previa de su identidad — el "preview de identidad" de la Fase 3.
 */
export default function BrandPresetSwitcher() {
  const { brandPresetId, setBrandPreset } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const active = brandPresets.find((p) => p.id === brandPresetId) ?? brandPresets[0];

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Cambiar tema del panel"
        aria-label="Cambiar tema del panel"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border border-ui-border bg-ui-bg pl-1.5 pr-3 py-1 hover:border-ui-accent/60 transition-colors"
      >
        <Swatch preset={active} size={18} />
        <span className="hidden sm:inline text-sm text-ui-text-muted">{active.label}</span>
        <Palette size={14} className="sm:hidden text-ui-text-subtle" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.13 }}
            className="absolute right-0 top-11 w-72 bg-ui-surface border border-ui-border rounded-xl shadow-xl z-50 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-ui-border">
              <p className="text-sm font-medium text-ui-text">Tema del panel</p>
              <p className="text-sm text-ui-text-subtle mt-0.5">Elige la identidad de color de tu panel.</p>
            </div>
            <div className="py-1.5">
              {brandPresets.map((preset) => {
                const isActive = preset.id === active.id;
                return (
                  <button
                    key={preset.id}
                    role="menuitemradio"
                    aria-checked={isActive}
                    onClick={() => { setBrandPreset(preset.id); setOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-ui-text/[0.06] transition-colors"
                  >
                    <Swatch preset={preset} size={28} />
                    <span className="min-w-0 flex-1">
                      <span
                        className="block text-base font-medium text-ui-text"
                        style={{ fontFamily: preset.typography.display }}
                      >
                        {preset.label}
                      </span>
                      {preset.description && (
                        <span
                          className="block text-sm text-ui-text-subtle truncate"
                          style={{ fontFamily: preset.typography.body }}
                        >
                          {preset.description}
                        </span>
                      )}
                    </span>
                    {isActive && <Check size={16} className="shrink-0 text-ui-accent" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Mini-previsualización de la identidad del preset: fondo + superficie + acento. */
function Swatch({ preset, size }: { preset: BrandPreset; size: number }) {
  const t = preset.tokens;
  return (
    <span
      className="shrink-0 inline-flex items-center justify-center rounded-lg overflow-hidden"
      style={{
        width: size,
        height: size,
        backgroundColor: `rgb(${t.uiBg})`,
        boxShadow: `inset 0 0 0 1px rgb(${t.uiBorderStrong})`,
      }}
      aria-hidden="true"
    >
      <span
        className="rounded-full"
        style={{ width: size * 0.42, height: size * 0.42, backgroundColor: `rgb(${t.uiAccent})` }}
      />
    </span>
  );
}
